<template>
  <div>
    <table v-if="rows.length > 0" class="certificati-table">
      <tr
        v-for="(certificato, index) in rows"
        :key="`cert-${index}`"
      >
        <td class="certificati-table__disciplina">
          <v-select
            v-if="certificato.isNew"
            v-model="certificato.disciplina"
            :items="getDisciplineOptions(index)"
            :placeholder="$t('select')"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="certificati-table__level"
            @change="onDisciplinaChange(certificato)"
          />
          <b v-else>{{ certificato.disciplinaText || certificato.disciplina || '-' }}</b>
        </td>
        <td class="certificati-table__select">
          <v-select
            v-model="certificato.livello"
            :items="Array.isArray(certificato.livelliAssegnabili) ? certificato.livelliAssegnabili : []"
            :placeholder="$t('select')"
            :disabled="!Array.isArray(certificato.livelliAssegnabili) || certificato.livelliAssegnabili.length === 0"
            dense
            outlined
            hide-details
            class="certificati-table__level"
          />
        </td>
        <td class="certificati-table__actions">
          <v-btn
            v-if="isRowPendingChanges(certificato, index)"
            icon
            large
            color="success"
            :disabled="isRowInvalid(certificato)"
            @click="saveLevel(certificato, index)"
          >
            <v-icon>mdi-content-save</v-icon>
          </v-btn>
          <v-btn
            icon
            large
            color="error"
            @click="removeDisciplina(certificato, index)"
          >
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </td>
      </tr>
    </table>
    <v-btn
      small
      outlined
      color="primary"
      class="mt-2"
      v-if="availableDisciplineCount > 0"
      @click="addDisciplina"
    >
      <v-icon left small>mdi-plus</v-icon>
      {{ $t("add") }}
    </v-btn>
  </div>
</template>

<script>
export default {
  name: "certificati-cell-component",
  props: {
    type: String,
    json: [Array, Object],
    disciplineItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      rows: [],
      initialSnapshot: "[]",
    };
  },
  watch: {
    json: {
      immediate: true,
      handler() {
        this.syncRowsFromJson();
      },
    },
  },
  computed: {
    hasPendingChanges() {
      return JSON.stringify(this.normalizedRows()) !== this.initialSnapshot;
    },
    normalizedDisciplineItems() {
      if (!Array.isArray(this.disciplineItems)) return [];

      return this.disciplineItems
        .map((item) => {
          if (item == null) return null;

          if (typeof item === "string" || typeof item === "number") {
            return {
              value: item,
              text: String(item),
            };
          }

          if (typeof item === "object") {
            const value =
              item.value != null ? item.value :
              item.idDisciplina != null ? item.idDisciplina :
              item.id != null ? item.id : null;
            const text =
              item.text != null ? item.text :
              item.descr != null ? item.descr :
              item.nome != null ? item.nome :
              value;

            if (value == null) return null;
            return {
              value,
              text: text != null ? text : String(value),
            };
          }

          return null;
        })
        .filter((item) => item && item.value != null);
    },
    availableDisciplineCount() {
      const used = new Set(
        this.rows
          .filter((row) => row && row.disciplina != null)
          .map((row) => String(row.disciplina).trim())
          .filter((value) => value !== "")
      );

      return this.normalizedDisciplineItems.filter((item) => !used.has(String(item.value))).length;
    },
    initialRowsMap() {
      let parsed = [];
      try {
        parsed = JSON.parse(this.initialSnapshot);
      } catch (error) {
        parsed = [];
      }

      return parsed.reduce((acc, row) => {
        if (!row || row.disciplina == null) return acc;
        acc[String(row.disciplina)] = row;
        return acc;
      }, {});
    },
    rowPendingStates() {
      return this.rows.map((row) => this.isRowChanged(row));
    },
    hasAnyRowPendingChanges() {
      return this.rowPendingStates.some(Boolean);
    },
  },
  methods: {
    normalizeRowForCompare(row) {
      if (!row || !row.disciplina) return null;
      return {
        disciplina: row.disciplina,
        disciplinaText: row.disciplinaText || this.getDisciplinaText(row.disciplina),
        livello: row.livello || null,
        livelliAssegnabili: Array.isArray(row.livelliAssegnabili) ? row.livelliAssegnabili : [],
      };
    },
    isRowInvalid(row) {
      return !row || !row.disciplina || String(row.disciplina).trim() === "";
    },
    isRowChanged(row) {
      const normalizedRow = this.normalizeRowForCompare(row);
      if (!normalizedRow) return false;

      const initialRow = this.initialRowsMap[String(normalizedRow.disciplina)];
      if (!initialRow) return true;

      return JSON.stringify(normalizedRow) !== JSON.stringify(initialRow);
    },
    isRowPendingChanges(certificato, index) {
      if (this.rowPendingStates[index]) {
        return true;
      }

      // For structural changes (e.g. row deletion), expose one save button.
      return !this.hasAnyRowPendingChanges && this.hasPendingChanges && index === 0;
    },
    syncRowsFromJson() {
      if (!Array.isArray(this.json)) {
        this.rows = [];
        this.initialSnapshot = "[]";
        return;
      }

      this.rows = this.json.map((certificato) => ({
        disciplina: certificato && certificato.disciplina ? certificato.disciplina : null,
        disciplinaText: certificato && certificato.disciplinaText ? certificato.disciplinaText : null,
        livello: certificato && certificato.livello ? certificato.livello : null,
        livelliAssegnabili: certificato && Array.isArray(certificato.livelliAssegnabili)
          ? certificato.livelliAssegnabili
          : [],
        isNew: false,
      }));
      this.initialSnapshot = JSON.stringify(this.normalizedRows());
    },
    normalizedRows() {
      return this.rows
        .map((row) => this.normalizeRowForCompare(row))
        .filter((row) => row !== null);
    },
    getDisciplinaText(disciplinaValue) {
      const found = this.normalizedDisciplineItems.find((item) => String(item.value) === String(disciplinaValue));
      return found ? found.text : disciplinaValue;
    },
    getDisciplineOptions(currentIndex) {
      const allOptions = this.normalizedDisciplineItems;
      const used = new Set(
        this.rows
          .filter((row, idx) => idx !== currentIndex)
          .map((row) => (row && row.disciplina != null ? String(row.disciplina).trim() : ""))
          .filter((value) => value !== "")
      );
      return allOptions.filter((item) => !used.has(String(item.value)));
    },
    onDisciplinaChange(certificato) {
      certificato.disciplinaText = this.getDisciplinaText(certificato.disciplina);
      if (Array.isArray(certificato.livelliAssegnabili) && !certificato.livelliAssegnabili.includes(certificato.livello)) {
        certificato.livello = null;
      }
    },
    addDisciplina() {
      if (this.availableDisciplineCount === 0) {
        return;
      }

      const livelliAssegnabili = this.rows.length > 0 && Array.isArray(this.rows[0].livelliAssegnabili)
        ? this.rows[0].livelliAssegnabili
        : [];
      this.rows.push({
        disciplina: null,
        disciplinaText: null,
        livello: null,
        livelliAssegnabili,
        isNew: true,
      });
    },
    removeDisciplina(certificato, index) {
      this.rows.splice(index, 1);
      this.emitSavePayload(certificato, index);
    },
    saveLevel(certificato, index) {
      if (this.isRowInvalid(certificato)) {
        return;
      }
      if (!this.isRowPendingChanges(certificato, index)) {
        return;
      }
      this.emitSavePayload(certificato, index);
    },
    emitSavePayload(certificato, index) {
      const payload = {
        index,
        disciplina: certificato ? certificato.disciplina : null,
        disciplinaText: certificato ? certificato.disciplinaText : null,
        livello: certificato ? certificato.livello : null,
        certificati: this.normalizedRows(),
      };
      this.$emit("save-level", payload);
      this.initialSnapshot = JSON.stringify(this.normalizedRows());
    },
    openCertificate(certificato, index) {
      const payload = {
        index,
        disciplina: certificato ? certificato.disciplina : null,
        disciplinaText: certificato ? certificato.disciplinaText : null,
        livello: certificato ? certificato.livello : null,
      };
      this.$emit("open-certificate", payload);
    },
  },
};
</script>

<style scoped>
.certificati-table {
  width: 100%;
}

.certificati-table td {
  padding: 2px 0;
  vertical-align: top;
}

.certificati-table tr + tr td {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding-top: 6px;
}

.certificati-table td.certificati-table__disciplina {
  padding-right: 8px;
  vertical-align: middle;
}

.certificati-table__select {
  min-width: 180px;
}

.certificati-table__level {
  max-width: 240px;
}

.certificati-table__actions {
  white-space: nowrap;
  padding-left: 8px;
}
</style>
